import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Mail, CheckCircle2 } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public opt-out page reached from the unsubscribe link in the WP campaign
 * emails. When arrived with a `?t=<token>`, we pre-fill the email so the
 * user just has to confirm.
 *
 * Only "no more emails of this type" is offered right now — that matches the
 * scope of the campaign. If future campaigns need granular controls the API
 * already accepts a `reason` field we can turn into per-topic toggles.
 */
export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [email, setEmail] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Resolve token → email on load
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/campaign/unsubscribe-info?t=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => { if (d?.email) setEmail(d.email); })
      .catch(() => {});
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !confirmed) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/campaign/unsubscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, reason, token: token || undefined }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      setDone(true);
    } catch (err) {
      toast.error("Impossible d'enregistrer la désinscription. Merci de réessayer ou de nous contacter à contact@puzzlevideo.fr.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-white text-[#0b1220] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg" data-testid="unsubscribe-page">
        {!done ? (
          <>
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[2px] text-[#3b92f0] uppercase mb-3">
                <Mail className="w-4 h-4" /> Options de communication
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Ne plus recevoir<br/>ce type d&rsquo;email.
              </h1>
              <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
                Confirmez votre adresse ci-dessous. Vous ne recevrez plus
                aucun email de la campagne d&rsquo;annonce du nouveau site.
                Vos éventuels emails transactionnels (devis, factures)
                restent inchangés.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-5 border border-neutral-200 p-6 rounded-none bg-white">
              <div>
                <Label htmlFor="unsub-email" className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                  Votre adresse email
                </Label>
                <Input
                  id="unsub-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom.nom@exemple.fr"
                  className="rounded-none mt-1"
                  data-testid="unsubscribe-email-input"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#3b92f0]"
                  data-testid="unsubscribe-confirm-checkbox"
                />
                <span className="text-sm leading-snug text-neutral-800 group-hover:text-black">
                  Je ne souhaite plus recevoir de mail de ce type.
                </span>
              </label>

              <div>
                <Label htmlFor="unsub-reason" className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                  Raison (facultatif)
                </Label>
                <Textarea
                  id="unsub-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Aidez-nous à nous améliorer…"
                  className="rounded-none mt-1"
                  data-testid="unsubscribe-reason-input"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-2">
                <Link to="/" className="text-sm text-neutral-500 hover:text-black transition-colors">
                  Annuler et revenir au site
                </Link>
                <Button
                  type="submit"
                  disabled={!email || !confirmed || submitting}
                  className="rounded-none bg-[#3b92f0] hover:bg-[#2578d1] text-white px-6 py-6 font-bold tracking-wide disabled:opacity-40"
                  data-testid="unsubscribe-submit-btn"
                >
                  {submitting ? "Enregistrement…" : "Confirmer la désinscription"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="text-center border border-neutral-200 p-10 rounded-none" data-testid="unsubscribe-success">
            <CheckCircle2 className="w-14 h-14 text-[#3b92f0] mx-auto mb-4" />
            <h2 className="text-2xl font-extrabold tracking-tight">Vous êtes désinscrit.</h2>
            <p className="mt-4 text-sm text-neutral-600 leading-relaxed">
              L&rsquo;adresse <strong className="text-black">{email}</strong> ne
              recevra plus d&rsquo;emails de cette campagne. Merci d&rsquo;avoir pris
              le temps de nous le faire savoir.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/" className="text-sm font-semibold text-[#3b92f0] hover:underline">
                Retour à l&rsquo;accueil →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
